import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './WidgetStyles.module.css';
import { ChevronDownIcon } from './WidgetIcons';
import type { TaskPlanState } from '../types/chat';

type TaskPlanPanelProps = {
    plan: TaskPlanState;
};

export function TaskPlanPanel({ plan }: TaskPlanPanelProps) {
    const { t } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const hasTasks = plan.tasks.length > 0;

    if (!hasTasks) {
        return null;
    }

    const completedTaskCount = plan.tasks.filter((task) => task.status === 'completed').length;
    const waitingTaskId = plan.tasks.find((task) => task.status === 'waiting_for_user')?.id;

    return (
        <section className={styles.taskPlanPanel} aria-live="polite">
            <div className={styles.taskPlanFrame}>
                <div className={styles.taskPlanContent}>
                    {isCollapsed ? (
                        <div className={styles.taskPlanCollapsedSummaryRow}>
                            <div className={styles.taskPlanCollapsedSummary}>
                                <PlanSummaryIcon />
                                <span className={styles.taskPlanSummaryText}>
                                    {plan.status === 'proposed'
                                        ? t('taskPlanAwaitingApprovalBadge', { defaultValue: 'Plan ready for approval' })
                                        : `${completedTaskCount} out of ${plan.tasks.length} tasks completed`}
                                </span>
                            </div>
                            {waitingTaskId ? <WaitingForInputBadge text={t('taskPlanWaitingForInputBadge')} /> : null}
                        </div>
                    ) : (
                        <ul className={styles.taskPlanList}>
                            {plan.tasks.map((task) => {
                                const isCurrent = plan.currentTaskId === task.id;
                                return (
                                    <li
                                        key={task.id}
                                        className={`${styles.taskPlanItem} ${isCurrent ? styles.taskPlanItemActive : ''}`}
                                        data-tooltip={getTaskTooltip(task, plan, t)}
                                    >
                                        <TaskIndicator task={task} isCurrent={isCurrent} />
                                        <div className={styles.taskPlanBody}>
                                            <div className={styles.taskPlanRow}>
                                                <span className={getTaskTitleClass(task, plan.currentTaskId)}>
                                                    {task.title}
                                                </span>
                                                {task.status === 'waiting_for_user' ? <WaitingForInputBadge text={t('taskPlanWaitingForInputBadge')} /> : null}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <button
                    className={styles.taskPlanToggle}
                    type="button"
                    data-state={isCollapsed ? 'collapsed' : 'expanded'}
                    aria-expanded={!isCollapsed}
                    aria-label={isCollapsed ? 'Expand plan' : 'Collapse plan'}
                    onClick={() => setIsCollapsed((previous) => !previous)}
                >
                    <ChevronDownIcon className={styles.taskPlanToggleIcon} />
                </button>
            </div>
        </section>
    );
}

type WaitingForInputBadgeProps = {
    text: string;
};

function WaitingForInputBadge({ text }: WaitingForInputBadgeProps) {
    return <span className={styles.taskPlanWaitingBadge}>{text}</span>;
}

function getTaskTitleClass(task: TaskPlanState['tasks'][number], currentTaskId?: string): string {
    const classes = [styles.taskPlanTaskTitle];

    if (currentTaskId && task.id === currentTaskId) {
        return classes.join(' ');
    }

    if (task.status === 'completed') {
        classes.push(styles.taskPlanTaskTitleCompleted);
        return classes.join(' ');
    }

    if (task.status === 'pending') {
        classes.push(styles.taskPlanTaskTitleMuted);
    }

    return classes.join(' ');
}

type TaskIndicatorProps = {
    task: TaskPlanState['tasks'][number];
    isCurrent: boolean;
};

function TaskIndicator({ task, isCurrent }: TaskIndicatorProps) {
    if (isCurrent) {
        return <span className={styles.taskPlanSpinner} aria-hidden="true" />;
    }

    return <span className={styles.taskPlanBullet} aria-hidden="true" />;
}

function PlanSummaryIcon() {
    return (
        <svg
            className={styles.taskPlanSummaryIcon}
            width="14"
            height="14"
            viewBox="0 0 14 14"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M5.2 2.5L3.6 4.1L2.8 3.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M6.8 3.8H11.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M5.2 7L3.6 8.6L2.8 7.8" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M6.8 8.3H11.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <circle cx="3.7" cy="11.6" r="1.1" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <path d="M6.8 11.6H11.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}

function getTaskTooltip(
    task: TaskPlanState['tasks'][number],
    plan: TaskPlanState,
    translate: (key: string) => string
): string | undefined {
    if (task.status === 'waiting_for_user') {
        return plan.waitingReason || translate('taskPlanWaitingForInputTooltip');
    }

    return undefined;
}
